import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { getAudioManager } from "@/audio/AudioManager";

export interface ButtonOptions {
  width?: number;
  height?: number;
  fillColor?: number;
  hoverColor?: number;
  textColor?: string;
  fontSize?: string;
  cornerRadius?: number;
  disabled?: boolean;
  /** Opts this one button out of the shared click sound - only for QA-only debug controls (see PR13-D's production audio integration spec section 10's "avoid sound on... QA debug buttons"). Every ordinary production button leaves this `false`. */
  silent?: boolean;
  /**
   * PR27: an optional larger INVISIBLE hit area, centered on the same
   * button - for a control whose drawn size should stay visually
   * restrained (a compact filter pill, say) but still wants a comfortable
   * ~44px mobile touch target. Only ever grows the interactive rectangle
   * (never shrinks it below `width`/`height`); the drawn rounded-rect
   * itself is completely unaffected.
   */
  minHitWidth?: number;
  minHitHeight?: number;
}

/** A `createButton()` result, with an escape hatch to toggle interactivity after creation - e.g. disabling SAVE while a request is in flight. Structurally a plain `Container` for every caller that doesn't need it. */
export interface ButtonHandle extends Phaser.GameObjects.Container {
  setEnabled(enabled: boolean): void;
}

/**
 * A single reusable pill button: rounded-rect graphics + centered label,
 * hover/press feedback, and a click callback. Used by the menu, level
 * select, game-over and level-complete screens so button look & feel
 * (and the small press animation) stays consistent everywhere.
 */
export function createButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  onClick: () => void,
  options: ButtonOptions = {},
): ButtonHandle {
  const width = options.width ?? 220;
  const height = options.height ?? 54;
  const fill = options.fillColor ?? GameConfig.colors.accent;
  const hover = options.hoverColor ?? GameConfig.colors.accentHover;
  const textColor = options.textColor ?? "#0f1117";
  const fontSize = options.fontSize ?? "20px";
  const radius = options.cornerRadius ?? 16;
  let disabled = options.disabled ?? false;

  const graphics = scene.add.graphics();
  const draw = (color: number) => {
    graphics.clear();
    graphics.fillStyle(color, disabled ? 0.4 : 1);
    graphics.fillRoundedRect(-width / 2, -height / 2, width, height, radius);
  };
  draw(fill);

  const label = scene.add
    .text(0, 0, text, {
      fontFamily: "Arial, sans-serif",
      fontSize,
      fontStyle: "bold",
      color: textColor,
    })
    .setOrigin(0.5)
    .setAlpha(disabled ? 0.6 : 1);

  const container = scene.add.container(x, y, [graphics, label]) as ButtonHandle;

  if (!disabled) {
    const hitWidth = Math.max(width, options.minHitWidth ?? 0);
    const hitHeight = Math.max(height, options.minHitHeight ?? 0);
    const hitArea = new Phaser.Geom.Rectangle(-hitWidth / 2, -hitHeight / 2, hitWidth, hitHeight);
    graphics.setInteractive({ hitArea, hitAreaCallback: Phaser.Geom.Rectangle.Contains, useHandCursor: true });
    graphics.on("pointerover", () => draw(hover));
    graphics.on("pointerout", () => draw(fill));
    graphics.on("pointerdown", () => {
      if (!options.silent) getAudioManager().onButtonClick();
      scene.tweens.add({
        targets: container,
        scale: 0.94,
        duration: 80,
        yoyo: true,
        onComplete: () => onClick(),
      });
    });
  }

  // Toggles input on/off without touching the hit area or re-registering
  // the hover/click listeners above - only meaningful for a button that
  // started enabled (`graphics.input` only exists once `setInteractive()`
  // has been called once); a button created with `disabled: true` has no
  // click behavior to turn back on and is unaffected.
  container.setEnabled = (enabled: boolean) => {
    disabled = !enabled;
    draw(fill);
    label.setAlpha(disabled ? 0.6 : 1);
    if (graphics.input) graphics.input.enabled = enabled;
  };

  return container;
}
